import * as React from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import type { CustomRouteComponentProps } from 'react-router-dom';
import type { InjectedFormProps } from 'redux-form';
import { Field, FieldArray } from 'redux-form';

import type ContactRM from 'ab-requestModels/contact/upsert';

import CLIENT from 'af-constants/routes/client';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import Input from 'af-fields/Input';

import type { OwnProps as AddressesProps } from 'af-components/SharedForms/Address/Addresses';
import Addresses from 'af-components/SharedForms/Address/Addresses';

import type { ContactAddressFM } from './formModel';
import styles from './styles.module.scss';
import type { OwnProps as EmailProps } from '../ContactMethod/EmailContactMethods';
import EmailContactMethods from '../ContactMethod/EmailContactMethods';
import type { OwnProps as PhoneProps } from '../ContactMethod/PhoneContactMethods';
import PhoneContactMethods from '../ContactMethod/PhoneContactMethods';

interface RemoveAddressOnDeleteProps extends SharedOwnProps {
	removeOnDelete: true;
}

interface UpdateAddressOnDeleteProps extends SharedOwnProps {
	removeOnDelete: false;
	selector: (fieldName: string) => ContactAddressFM;
}

interface SharedOwnProps extends InjectedFormProps<ContactRM> {
	companyName: string;
	hideButtons?: boolean;
	emailPhoneError?: string;
	onSubmit: (form: ContactRM) => Promise<void>;
	areEmailsRequired?: boolean;
}

type OwnProps = RemoveAddressOnDeleteProps | UpdateAddressOnDeleteProps;

interface PathParams {
	contactId: string;
}

type Props = OwnProps & (CustomRouteComponentProps<PathParams> | CustomRouteComponentProps);

interface State {
	removedEmails: ContactRM['emails'];
	removedPhones: ContactRM['phones'];
	removedAddresses: ContactRM['addresses'];
}

class ContactForm extends React.PureComponent<Props, State> {
	static defaultProps: Partial<Props> = {
		hideButtons: false,
	};

	state: State = {
		removedEmails: [],
		removedPhones: [],
		removedAddresses: [],
	};

	removeEmail = (email: ContactRM['emails'][0]) => {
		const { touch } = this.props;
		touch?.('emails');
		this.setState((state) => ({ removedEmails: [...state.removedEmails, email] }));
	};

	removePhone = (phone: ContactRM['phones'][0]) => {
		const { touch } = this.props;
		touch?.('phones');
		this.setState((state) => ({ removedPhones: [...state.removedPhones, phone] }));
	};

	removeAddress = (address: ContactRM['addresses'][0]) => {
		this.setState((state) => ({ removedAddresses: [...state.removedAddresses, address] }));
	};

	submit = async (form: ContactRM) => {
		const { onSubmit } = this.props;
		const { removedAddresses, removedPhones, removedEmails } = this.state;
		await onSubmit({
			...form,
			emails: [...(form.emails ?? []), ...removedEmails],
			phones: [...(form.phones ?? []), ...removedPhones],
			addresses: [...(form.addresses ?? []), ...removedAddresses],
		});
	};

	renderAddresses = () => {
		const { form, removeOnDelete, change } = this.props;

		if (removeOnDelete === false) {
			const { selector } = this.props;
			return (
				<FieldArray<AddressesProps>
					canBeEmpty={true}
					change={change}
					component={Addresses}
					formName={form}
					name="addresses"
					onAddressRemove={this.removeAddress}
					removeOnDelete={removeOnDelete}
					selector={selector}
				/>
			);
		}

		return (
			<FieldArray<AddressesProps>
				canBeEmpty={true}
				change={change}
				component={Addresses}
				formName={form}
				name="addresses"
				onAddressRemove={this.removeAddress}
				removeOnDelete={removeOnDelete}
			/>
		);
	};

	goBack = () => {
		const {
			location: { state: { orgAlias } },
			companyName,
			history,
		} = this.props;
		history.push(CLIENT.COMPANY.CONTACTS.LIST(orgAlias, companyName));
	};

	render() {
		const {
			submitting,
			invalid,
			emailPhoneError,
			anyTouched,
			handleSubmit,
			change,
			hideButtons,
			areEmailsRequired,
		} = this.props;
		const disableSubmit = invalid || !!emailPhoneError;
		return (
			<Form>
				<div className="form-box-highlighted-input">
					<Row className="row--padded-top">
						<Col sm={8}>
							<Field
								className="contact__item"
								component={Input}
								isRequired={true}
								label="Full name"
								name="fullName"
								placeholder="Enter Contact Name"
								type="text"
							/>
						</Col>
						<Col sm={8}>
							<Field
								component={Input}
								label="Title"
								name="title"
								placeholder="Title"
								type="text"
							/>
						</Col>
						<Col sm={8}>
							<Field
								component={Input}
								label="Company name"
								name="companyName"
								placeholder="Company name"
								type="text"
							/>
						</Col>
					</Row>
				</div>
				<div>
					<FieldArray<EmailProps>
						areEmailsRequired={areEmailsRequired}
						change={change}
						component={EmailContactMethods}
						customError={anyTouched ? emailPhoneError : undefined}
						name="emails"
						onRemoveContactMethod={this.removeEmail}
					/>
					<FieldArray<PhoneProps>
						change={change}
						component={PhoneContactMethods}
						customError={anyTouched ? emailPhoneError : undefined}
						name="phones"
						onRemoveContactMethod={this.removePhone}
					/>
				</div>
				<div className="contact-method contact-method--non-padded">
					<div className="contact-method__header m-t-m m-b-m">
						<SegmentLabel
							label="Addresses"
							tooltip="Physical addresses associated with this contact, including street, city, state, zip, P.O. Box, and country."
						/>
					</div>
					<div>
						{this.renderAddresses()}
					</div>
				</div>
				{!hideButtons &&
					<div className={styles.footerActions}>
						<Button
							label="Cancel"
							onClick={this.goBack}
							style="secondary"
							type="button"
						/>
						<Button
							disabled={disableSubmit}
							label="Submit"
							onClick={handleSubmit(this.submit)}
							style="primary"
							submitting={submitting}
						/>
					</div>
				}
			</Form>
		);
	}
}

export default ContactForm;
